import { isTenantScopeRequired } from "../contexts/tenantScopeHeaders";
import { useStytchB2BClient } from "@stytch/react/b2b";
import { useCallback } from "react";
import { useNavigate, type NavigateFunction } from "react-router-dom";
import { usePlatform } from "../platform/PlatformProvider";
import { useEmbedAuth } from "../contexts/EmbedAuthContext";
import { performLogout } from "../utils/logout";
import { announceSignOut } from "../utils/signOutBroadcast";
import { endProjectSessions } from "../api/sessionSignOut";
import { revokeSourceSession } from "../auth/sourceSession";
import { isNativeAuthAvailable } from "../native/nativeAuth";

/**
 * The longest sign-out waits for the stored caches to clear before it leaves. The query cache
 * bounds its own wait at 5 s, and its snapshots were made unreadable before any of it started
 * (`performLogout`); what has not finished goes on while the page stays.
 */
const CLEANUP_BUDGET_MS = 6_000;

/** Whether `work` settled within `ms`. */
async function settledWithin(work: Promise<unknown>, ms: number): Promise<boolean> {
    let timer: ReturnType<typeof setTimeout> | undefined;
    const late = new Promise<false>((resolve) => {
        timer = setTimeout(() => resolve(false), ms);
    });
    const settled = work.then(
        () => true,
        () => true,
    );
    return Promise.race([settled, late]).finally(() => clearTimeout(timer));
}

/**
 * Custom hook for handling user logout.
 * Ends the project-URL sessions exchanged from this sign-in, clears caches, revokes the Stytch
 * session (through the shell where the shell holds the sign-in), tells this browser's other
 * tabs, clears auth tokens, and leaves for sign-in.
 *
 * @returns Async logout function with a stable identity across renders
 *
 * @example
 * const logout = useLogout();
 * await logout();
 */
export function useLogout() {
    return useSignOut(useNavigate());
}

/**
 * The same sign-out for a screen above every router (admission's own waiting and failure
 * screens, which a host with per-tab routers mounts above them): it always leaves by a full load.
 */
export function useLogoutWithoutRouter() {
    return useSignOut(null);
}

function useSignOut(navigate: NavigateFunction | null) {
    const platform = usePlatform();
    const { setTokens } = useEmbedAuth();
    const client = useStytchB2BClient();

    const logout = useCallback(
        async (options?: { hardRedirect?: boolean }) => {
            // A shell that holds the sign-in outside the page (the iOS Keychain, the desktop's
            // vault) ends it there.
            const shellLogout = platform.auth.logout;
            // First, while this browser's sign-in still authenticates it: the server ends the
            // project sessions exchanged from that sign-in. Bounded and best effort.
            const projectSessionsEnded = endProjectSessions();
            if (shellLogout) {
                await projectSessionsEnded;
                // Revoke the shell's long-lived session and wipe the pinned offline library —
                // explicit Sign Out, so a shared/returned device must not carry this member's
                // data forward.
                await shellLogout({ wipeOfflineData: true }).catch(() => undefined);
            }
            const cacheCleanup = performLogout(); // Invalidate caches before the first await.
            let fullLoad = !!shellLogout || !!options?.hardRedirect || isTenantScopeRequired();
            // End the Stytch session here — without this, /authenticate sees a live session and
            // bounces straight back into the app. Bounded: with no answer from Stytch the
            // sign-in is forgotten here instead, and this page, whose SDK still holds it in
            // memory, leaves by a full load.
            if (!shellLogout) {
                await projectSessionsEnded;
                if ((await revokeSourceSession(client)) === "forgotten") fullLoad = true;
            }
            // Only once the sign-in is gone here: a project tab that reloads on hearing it must
            // meet sign-in, not re-admit on a sign-in still stored.
            announceSignOut();
            if (!(await settledWithin(cacheCleanup, CLEANUP_BUDGET_MS)))
                console.warn("[Logout] Stored caches are still clearing; signing out anyway");
            setTokens(null); // Clear auth tokens
            // A shell-held sign-out needs a FULL reload, not an SPA navigate: it tears down
            // the headless ConversationEngine module and any other in-flight JS from the
            // outgoing member, same as the native org-switch/environment reload path
            // (NativeNavBridge's orgSwitched/setEnvironment listeners) — without it, a slow
            // in-flight request can still land after `setTokens(null)` and write into native's
            // session-less EngineState mirror. The ordinary web logout button keeps the SPA
            // navigate. The desktop shell shows its own sign-in at its root; iOS and the web
            // sign in at /authenticate.
            if (shellLogout && platform.auth.resetAfterSignOut) {
                // A host of several pages puts every window back to one signed-out page.
                await platform.auth.resetAfterSignOut().catch(() => {
                    window.location.href = "/";
                });
            } else if (shellLogout && !isNativeAuthAvailable()) {
                window.location.href = "/";
            } else if (fullLoad || !navigate) {
                window.location.href = "/authenticate";
            } else {
                navigate("/authenticate"); // Redirect to login
            }
        },
        [client, navigate, platform, setTokens],
    );

    return logout;
}
